import { LOCALSTORAGE_KEYS } from "@circle-react/config/Settings";
import { find } from "lodash";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";

import { useLocalStorage } from "@/react/hooks/utils/useLocalStorage";

import { spaceGroupQueries } from "../lib/queries/spaceGroups";
import type { SpaceGroupsApiResponse } from "../lib/queries/spaceGroups";
import type { SpaceGroup } from "../types";
import type { LoadingStatus } from "./loadingStatuses";
import { ERROR, LOADED, LOADING, REFRESHING } from "./loadingStatuses";
import { usePunditUserContext } from "./punditUserContext";

// In long run we may have multiple states for status: loading, reloading, etc.

interface SpaceGroupContextStateType {
  actions: {
    loadData: () => Promise<void>;
    refresh: () => void;
  };
  helpers: {
    isDataLoading: () => boolean;
    isDataRefreshing: () => boolean;
    isDataLoaded: () => boolean;
    findById: (id: string) => SpaceGroup | undefined;
    findBy: (conditions: object) => SpaceGroup | undefined;
  };
  isLoading: boolean;
  records: SpaceGroup[];
  status: LoadingStatus;
}

const initialState: SpaceGroupContextStateType = {
  status: LOADING,
  records: [],
  actions: {
    loadData: () => Promise.resolve(),
    refresh: () => {},
  },
  helpers: {
    isDataLoading: () => false,
    isDataRefreshing: () => false,
    isDataLoaded: () => false,
    findById: () => undefined,
    findBy: () => undefined,
  },
  isLoading: false,
};

const SpaceGroupsContext =
  createContext<SpaceGroupContextStateType>(initialState);
SpaceGroupsContext.displayName = "SpaceGroupsContext";

export interface SpaceGroupsContextProviderProps {
  children: ReactNode;
  spaceGroups?: SpaceGroup[];
}

export const SpaceGroupsContextProvider = ({
  children,
  spaceGroups,
}: SpaceGroupsContextProviderProps) => {
  const [status, setStatus] = useState(LOADING);
  const [records, setRecords] = useState<SpaceGroup[]>([]);
  const { currentCommunityMember, data } = usePunditUserContext();
  const { getValue: getLocalStorageValue, setValue: setLocalStorageValue } =
    useLocalStorage<SpaceGroup[]>(LOCALSTORAGE_KEYS.SpaceGroupsContextProvider);

  const onDataLoadSuccess = useCallback(
    (data: SpaceGroupsApiResponse) => {
      setRecords(data.records);
      setStatus(LOADED);
      setLocalStorageValue(data.records);
    },
    [setLocalStorageValue],
  );

  const hasProvidedSpaceGroups = spaceGroups !== undefined;
  const hasPunditData = !!currentCommunityMember || !!data.current_community;
  const canFetchSpaceGroups =
    !!currentCommunityMember || data?.current_community?.is_private === false;
  const shouldFetchSpaceGroups =
    !hasProvidedSpaceGroups && hasPunditData && canFetchSpaceGroups;

  const {
    data: queryData,
    error: queryError,
    refetch,
  } = useQuery({
    ...spaceGroupQueries.list(),
    enabled: shouldFetchSpaceGroups,
  });

  const loadData = useCallback(async () => {
    if (!hasPunditData) {
      setStatus(LOADING);
      return;
    }

    if (!canFetchSpaceGroups) {
      setRecords([]);
      setStatus(LOADED);
      return;
    }

    const { data: refetchedData, error: refetchError } = await refetch();
    if (refetchError) {
      setStatus(ERROR);
    } else if (refetchedData) {
      onDataLoadSuccess(refetchedData);
    }
  }, [hasPunditData, canFetchSpaceGroups, refetch, onDataLoadSuccess]);

  useEffect(() => {
    if (hasProvidedSpaceGroups) {
      setRecords(spaceGroups);
      setStatus(LOADED);
      return;
    }

    if (!hasPunditData) {
      setStatus(LOADING);
      return;
    }

    if (!canFetchSpaceGroups) {
      setRecords([]);
      setStatus(LOADED);
      return;
    }

    const localStorageSpaceGroups = getLocalStorageValue();
    if (localStorageSpaceGroups && localStorageSpaceGroups.length > 0) {
      setRecords(localStorageSpaceGroups);
      setStatus(LOADED);
    } else {
      setStatus(LOADING);
    }
  }, [
    hasProvidedSpaceGroups,
    spaceGroups,
    hasPunditData,
    canFetchSpaceGroups,
    getLocalStorageValue,
  ]);

  useEffect(() => {
    if (hasProvidedSpaceGroups || !canFetchSpaceGroups) {
      return;
    }

    if (queryData) {
      onDataLoadSuccess(queryData);
    } else if (queryError) {
      setStatus(ERROR);
    }
  }, [
    hasProvidedSpaceGroups,
    canFetchSpaceGroups,
    queryData,
    queryError,
    onDataLoadSuccess,
  ]);

  const refresh = useCallback(() => {
    setStatus(REFRESHING);
    void loadData();
  }, [loadData]);

  const isDataLoaded = useCallback(() => status === LOADED, [status]);
  const isDataLoading = useCallback(() => status === LOADING, [status]);
  const isDataRefreshing = useCallback(() => status === REFRESHING, [status]);

  // uses: https://lodash.com/docs/4.17.15#find
  const findBy = useCallback(
    (conditions: object = {}): SpaceGroup | undefined => {
      if (!isDataLoaded()) {
        console.warn(`findBy called when status = ${status}.`);
      }
      const result = find(records, conditions);
      if (result && typeof result === "object" && "id" in result) {
        return result;
      }
      return undefined;
    },
    [isDataLoaded, status, records],
  );

  const findById = useCallback(
    (id: string): SpaceGroup | undefined => findBy({ id }),
    [findBy],
  );

  const value: SpaceGroupContextStateType = useMemo(
    () => ({
      status,
      records,
      actions: { loadData, refresh },
      isLoading: isDataLoading(),
      helpers: {
        isDataLoading,
        isDataRefreshing,
        isDataLoaded,
        findById,
        findBy,
      },
    }),
    [
      status,
      records,
      loadData,
      refresh,
      isDataLoading,
      isDataRefreshing,
      isDataLoaded,
      findById,
      findBy,
    ],
  );
  return (
    <SpaceGroupsContext.Provider value={value}>
      {children}
    </SpaceGroupsContext.Provider>
  );
};

export const useSpaceGroupsContext = (): SpaceGroupContextStateType =>
  useContext(SpaceGroupsContext);
